import React, { memo, useMemo } from 'react';
import { areEqual } from 'Utils/equalityChecks';

import { Table, TableBody, TableColumn, TableHeader, TableRow, Th } from 'Components/Table';

import { Icon } from 'Components/Icons';
import { TimeTrackingCardTimesheet } from 'Containers/TimeTracking/types';
import { formatDate, formatCurrency } from 'Utils/helpers';
import { parseNumber } from 'Utils/numbers';

import { formatDurationForTimeTracking } from 'Containers/TimeTracking/helpers';
import { Duration } from 'date-fns';

import classes from './timesheetsList.module.css';

const sumTotalHours = (cards: TimeTrackingCardTimesheet[]): Duration => {
  let total = 0;
  cards.forEach((card) => {
    if (card.totalHours != null) {
      total += card.totalHours;
    } else if (card.duration) {
      total += (card.duration.days ?? 0) * 24 + (card.duration.hours ?? 0) + (card.duration.minutes ?? 0) / 60;
    }
  });
  const hours = Math.floor(total);
  const minutes = Math.round((total - hours) * 60);
  return { hours, minutes };
};

interface Props {
  sortBy?: string;
  searchValue?: string;
  timeCards?: any;
  isCompanyAdmin?: boolean;
  currency?: string;
  onClickProject?: (e: any) => void;
  onClickNotes?: (e: any) => void;
  onClickEdit?: (e: any) => void;
  onClickDelete?: (e: any) => void;
  onClickSort?: (sort: string) => void;
}

const sortIcon = (sortBy: string, field: string) => {
  if (sortBy === `-${field}`) return <Icon className={classes.sortIcon} type="caretdown" />;
  if (sortBy === field) return <Icon className={classes.sortIcon} type="caretup" />;
  return null;
};

const TimesheetsList = ({
  sortBy,
  searchValue,
  timeCards,
  isCompanyAdmin,
  currency,
  onClickProject,
  onClickNotes,
  onClickEdit,
  onClickDelete,
  onClickSort,
}: Props) => {
  const totalDuration = useMemo(
    () => (timeCards?.data?.length > 0 ? sumTotalHours(timeCards.data) : null),
    [timeCards?.data]
  );

  // Server provides the authoritative cost rollup over the full filtered set (pre-pagination),
  // so totals stay correct across pages and multi-day-split rows.
  const costTotal = timeCards?.meta?.cost_total ?? null;
  const unpricedCount = timeCards?.meta?.unpriced_count ?? 0;

  const showCostColumn = isCompanyAdmin && currency;

  return timeCards?.data?.length > 0 ? (
    <>
    <Table className={`table ${classes.projectListWrapper}`}>
      <TableHeader>
        <TableRow>
          <Th>
            <span
              className={classes.thSpan}
              role="button"
              tabIndex={-1}
              onClick={() => onClickSort('-user')}
              onKeyUp={() => onClickSort('-user')}
            >
              Employee Name
              {sortIcon(sortBy, 'user')}
            </span>
          </Th>
          <Th>
            <span
              className={classes.thSpan}
              role="button"
              tabIndex={-1}
              onClick={() => onClickSort('-address')}
              onKeyUp={() => onClickSort('-address')}
            >
              Project Address
              {sortIcon(sortBy, 'address')}
            </span>
          </Th>
          <Th>
            <span
              className={classes.thSpan}
              role="button"
              tabIndex={-1}
              onClick={() => onClickSort('-project_number')}
              onKeyUp={() => onClickSort('-project_number')}
            >
              Project Number
              {sortIcon(sortBy, 'project_number')}
            </span>
          </Th>
          <Th>
            <span
              className={classes.thSpan}
              role="button"
              tabIndex={-1}
              onClick={() => onClickSort('-time_in')}
              onKeyUp={() => onClickSort('-time_in')}
            >
              Date In
              {sortIcon(sortBy, 'time_in')}
            </span>
          </Th>
          <Th>
            <span
              className={classes.thSpan}
              role="button"
              tabIndex={-1}
              onClick={() => onClickSort('-duration')}
              onKeyUp={() => onClickSort('-duration')}
            >
              Hours
              {sortIcon(sortBy, 'duration')}
            </span>
          </Th>
          {showCostColumn && <Th>Cost</Th>}
          <Th>Notes</Th>
          <Th>Actions</Th>
        </TableRow>
      </TableHeader>
      <TableBody>
        {timeCards.data.map((card: TimeTrackingCardTimesheet) => (
          <TableRow key={`${card.id}${card.cardDay ? `-${card.cardDay}` : ''}`}>
            <TableColumn>
              <div>{card.user}</div>
            </TableColumn>

            {card.projectId ? (
              <TableColumn
                dataId={parseNumber(card.projectId)}
                className={classes.columnContent}
                tdOnClick={onClickProject}
              >
                <div className={classes.address}>
                  <p className={classes.street}>{card.address}</p>
                  <p className={classes.region}>{card.address2}</p>
                </div>
              </TableColumn>
            ) : (
              <TableColumn className={classes.columnContent}>
                <div className={classes.address}>
                  <p className={classes.street}>[deleted project]</p>
                </div>
              </TableColumn>
            )}

            <TableColumn className={classes.columnContent}>{card.projectNumber}</TableColumn>
            <TableColumn className={classes.columnContent}>{formatDate(card.timeIn, 'PP')}</TableColumn>
            <TableColumn>{formatDurationForTimeTracking(card.duration)}</TableColumn>
            {showCostColumn && (
              <TableColumn className={classes.columnContent}>
                {card.cost != null ? formatCurrency(card.cost, currency) : '-'}
              </TableColumn>
            )}
            <TableColumn>
              {card.notesCount > 0 && (
                <span role="button" data-id={card.id} onClick={onClickNotes} onKeyUp={onClickNotes} tabIndex={0}>
                  <Icon type="notesbutton" />
                </span>
              )}
            </TableColumn>
            <TableColumn>
              <span role="button" data-id={card.id} onClick={onClickEdit} onKeyUp={onClickEdit} tabIndex={0}>
                <Icon type="editbutton" />
              </span>
              <span role="button" data-id={card.id} onClick={onClickDelete} onKeyUp={onClickDelete} tabIndex={0}>
                <Icon type="deletebutton" />
              </span>
            </TableColumn>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    {totalDuration && (
      <div className={classes.totalBar}>
        <span className={classes.totalLabel}>Total</span>
        <span className={classes.totalValue}>{formatDurationForTimeTracking(totalDuration)}</span>
        {showCostColumn && costTotal != null && (
          <>
            <span className={classes.totalDivider}>|</span>
            <span className={classes.totalLabel}>Cost Total</span>
            <span className={classes.totalValue}>{formatCurrency(costTotal, currency)}</span>
            {unpricedCount > 0 && (
              <span className={classes.unpricedBadge}>({unpricedCount} unpriced)</span>
            )}
          </>
        )}
      </div>
    )}
    </>
  ) : (
    <>
      <Table className={`table ${classes.projectListWrapper}`}>
        <TableHeader>
          <TableRow>
            <Th>
              <span
                className={classes.thSpan}
                role="button"
                tabIndex={-1}
                onClick={() => onClickSort('-uid')}
                onKeyUp={() => onClickSort('-uid')}
              >
                Employee Name
                {sortBy === '-uid' ? (
                  <Icon className={classes.sortIcon} type="caretup" />
                ) : (
                  <Icon className={classes.sortIcon} type="caretdown" />
                )}
              </span>
            </Th>
            <Th>
              <span
                className={classes.thSpan}
                role="button"
                tabIndex={-1}
                onClick={() => onClickSort('-uid')}
                onKeyUp={() => onClickSort('-uid')}
              >
                Project Address
                {sortBy === '-uid' ? (
                  <Icon className={classes.sortIcon} type="caretup" />
                ) : (
                  <Icon className={classes.sortIcon} type="caretdown" />
                )}
              </span>
            </Th>
            <Th>
              <span
                className={classes.thSpan}
                role="button"
                tabIndex={-1}
                onClick={() => onClickSort('-uid')}
                onKeyUp={() => onClickSort('-uid')}
              >
                Project Number
                {sortBy === '-uid' ? (
                  <Icon className={classes.sortIcon} type="caretup" />
                ) : (
                  <Icon className={classes.sortIcon} type="caretdown" />
                )}
              </span>
            </Th>
            <Th>
              <span
                className={classes.thSpan}
                role="button"
                tabIndex={-1}
                onClick={() => onClickSort('-uid')}
                onKeyUp={() => onClickSort('-uid')}
              >
                Date In
                {sortBy === '-uid' ? (
                  <Icon className={classes.sortIcon} type="caretup" />
                ) : (
                  <Icon className={classes.sortIcon} type="caretdown" />
                )}
              </span>
            </Th>
            <Th>
              <span
                className={classes.thSpan}
                role="button"
                tabIndex={-1}
                onClick={() => onClickSort('-uid')}
                onKeyUp={() => onClickSort('-uid')}
              >
                Hours
                {sortBy === '-uid' ? (
                  <Icon className={classes.sortIcon} type="caretup" />
                ) : (
                  <Icon className={classes.sortIcon} type="caretdown" />
                )}
              </span>
            </Th>
            {showCostColumn && <Th>Cost</Th>}
            <Th>Notes</Th>
            <Th>Actions</Th>
          </TableRow>
        </TableHeader>
      </Table>
      <div
        className={`d-flex justify-content-center align-items-center flex-column w-100 ${classes.noProjectsContent}`}
      >
        <p className={classes.noProjectsText}>
          {searchValue?.length >= 2 ? `No timecards found for "${searchValue}".` : 'No timecards found.'}
        </p>
        <Icon type="rocketemblemtwo" />
      </div>
    </>
  );
};

TimesheetsList.defaultProps = {
  searchValue: null,
  sortBy: null,
  timeCards: [],
  isCompanyAdmin: false,
  currency: 'USD',
  onClickProject: null,
  onClickNotes: null,
  onClickEdit: null,
  onClickDelete: null,
  onClickSort: null,
};

const TimesheetsListMemo = memo(TimesheetsList, areEqual);

export { TimesheetsListMemo as TimesheetsList };
